import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import {
  ArrowPathRoundedSquareIcon,
  ArrowTopRightOnSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { TableToolbar } from "../../../../components/utils/TableToolbar";
import { useTableView } from "../../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../../components/utils/PMTableView/RightDrawer";
import {
  CustomerTitle,
  ProjectTitle,
  dateToString,
  updateUrlWithException,
} from "../../../../components/Utils";
import { getChecklistsAsync } from "../../../../slices/quality/checklist";
import { createChecklist, fetchChecklists } from "../../../../api/quality/checklist";
import { EnhancedChecklistSelector } from "../../../../components/dashboard/service/reports/enhanced-checklist-selector";
import {
  ChecklistsDataTable,
  getChecklistColumns,
} from "../../../../components/dashboard/quality/checklists/checklistsTable";
import {
  ChecklistApprovedStatus,
  ChecklistStatus,
  checklistStatusOptions,
} from "../../../../components/dashboard/quality/checklists/utils";

const CHECKLIST_LIST_FIELDS =
  "id,number,displayTitle,title,description,project,customer,checklist_type,folder,status,approved,approved_by,responsible_executive,created_at";

export const ChecklistsList = ({ createSelectorProps = null } = {}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam);
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    return [{ name: "status", value: JSON.stringify(["open", "in_progress"]) }];
  };

  const [drawerChecklist, setDrawerChecklist] = useState(null);

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields: CHECKLIST_LIST_FIELDS,
  });

  const itemState = useSelector((store) => store.checklists);
  const { isLoading, totalCount } = itemState;

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller]);

  // Column definitions + table-view state
  const columns = useMemo(() => getChecklistColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "checklists",
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    const backendFilters = [];
    if (filters.checklistStatuses?.length > 0) {
      backendFilters.push({
        name: "status",
        value: JSON.stringify(filters.checklistStatuses),
      });
    }
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: backendFilters,
    }));
  };

  const getInitialFilters = () => {
    const initial = { query: controller.query || "", checklistStatuses: [] };
    (controller.filters || []).forEach((filter) => {
      if (filter.name !== "status") return;
      try {
        const values = JSON.parse(filter.value);
        initial.checklistStatuses = Array.isArray(values)
          ? values.map(String)
          : [String(values)];
      } catch (e) {
        initial.checklistStatuses = [String(filter.value)];
      }
    });
    return initial;
  };

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getChecklistsAsync({ ...controller, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((ctrl) => {
    dispatch(getChecklistsAsync(ctrl));
  }, 250);

  useEffect(() => {
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [checklistDefaultTitle, setChecklistDefaultTitle] = useState("");
  const [isCreatingChecklist, setIsCreatingChecklist] = useState(false);

  useEffect(() => {
    if (!checklistDialogOpen || !createSelectorProps) {
      return undefined;
    }

    let cancelled = false;

    const loadDefaultTitle = async () => {
      try {
        const res = await fetchChecklists({
          per_page: 1,
          page: 1,
          sortBy: "created_at",
          sort: "DESC",
        });
        const existing = res?.totalCount || 0;
        const title = t("Generic new checklist title", {
          number: existing + 1,
          date: new Date().toISOString().split("T")[0],
        });
        if (!cancelled) {
          setChecklistDefaultTitle(title);
        }
      } catch (err) {
        console.error("Could not resolve default checklist title:", err);
        if (!cancelled) {
          setChecklistDefaultTitle(
            t("Generic new checklist title", {
              number: 1,
              date: new Date().toISOString().split("T")[0],
            })
          );
        }
      }
    };

    loadDefaultTitle();

    return () => {
      cancelled = true;
    };
  }, [checklistDialogOpen, createSelectorProps, t]);

  const handleChecklistSelect = useCallback(
    async (config) => {
      const title = String(config?.title || checklistDefaultTitle || "").trim();
      if (!title) {
        toast.error(t("Title is required"));
        return;
      }

      setIsCreatingChecklist(true);
      try {
        const payload = {
          title,
          description: "",
          date:
            config?.context?.date || new Date().toISOString().split("T")[0],
          status: "open",
        };

        if (config?.context?.customer_id) {
          payload.customer_id = config.context.customer_id;
        }
        if (config?.context?.project_id) {
          payload.project_id = config.context.project_id;
        }
        if (config?.context?.folder_id) {
          payload.folder_id = config.context.folder_id;
        }
        if (config?.context?.responsible_executive_id) {
          payload.responsible_executive_id = config.context.responsible_executive_id;
        }

        if (config?.mode === "type") {
          payload.checklist_type_id = config.checklist_type_id;
          if (config.selected_item_ids?.length) {
            payload.selected_item_ids = config.selected_item_ids;
          }
        } else if (config?.mode === "main_report" && config.items?.length) {
          payload.copy_from_checklist_items = config.items;
        }

        const response = await createChecklist(payload);

        if (response?.response?.status >= 400) {
          const apiErrors = response.response?.data?.errors;
          throw new Error(
            Array.isArray(apiErrors)
              ? apiErrors.join(", ")
              : t("Failed to create checklist")
          );
        }

        const checklistId = response?.id || response?.data?.id;

        if (checklistId) {
          toast.success(t("Checklist created successfully"));
          setChecklistDialogOpen(false);
          dispatch(getChecklistsAsync({ ...controller, page: 1, append: false }));
          setController((c) => ({ ...c, page: 1 }));
          navigate(`/dashboard/hse-ks/checklists/${checklistId}`);
        } else {
          throw new Error(t("Failed to create checklist"));
        }
      } catch (err) {
        console.error("Error creating checklist:", err);
        toast.error(err?.message || t("Failed to create checklist"));
      } finally {
        setIsCreatingChecklist(false);
      }
    },
    [checklistDefaultTitle, controller, dispatch, navigate, t]
  );

  const listCount = totalCount != null ? `${totalCount}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("Sjekklister")}
        subtitle="Oversikt over sjekklister (ISO 9001:2015 - kap. 8.5)"
        listCount={listCount}
        actions={[]}
        mainButton={{
          label: t("New"),
          action: () => setChecklistDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="p-5 space-y-4">
          {/* Filter card */}
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={getInitialFilters()}
            searchPlaceholder="Søk i sjekklister"
            filterTitle="Filtrer sjekklister"
            hideStatusFilter={true}
            customDropdownFilters={[
              {
                key: "checklistStatuses",
                label: t("Statuses"),
                options: checklistStatusOptions.map((status) => ({
                  value: String(status.value),
                  label: status.label,
                })),
              },
            ]}
          />

          {/* Table card */}
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <ChecklistsDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              density={tableView.density}
              hasMore={(totalCount || 0) > (itemState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(checklist) => setDrawerChecklist(checklist)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerChecklist}
        onClose={() => setDrawerChecklist(null)}
        eyebrow={
          drawerChecklist ? `${t("Number")} ${drawerChecklist.number || ""}` : ""
        }
        title={
          drawerChecklist?.displayTitle ||
          drawerChecklist?.title ||
          t("Checklist")
        }
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () =>
              navigate(
                `/dashboard/hse-ks/checklists/${drawerChecklist.id}`
              ),
          },
        ]}
      >
        {drawerChecklist && (
          <DrawerSection>
            <DrawerProps
              items={[
                {
                  label: t("Status"),
                  value: <ChecklistStatus checklist={drawerChecklist} />,
                },
                {
                  label: t("Approved Status"),
                  value: drawerChecklist.approved ? (
                    <ChecklistApprovedStatus checklist={drawerChecklist} />
                  ) : (
                    "—"
                  ),
                },
                {
                  label: t("Type"),
                  value: drawerChecklist.checklist_type?.displayTitle || "—",
                },
                {
                  label: t("project"),
                  value: (
                    <ProjectTitle
                      project={drawerChecklist?.project}
                      folder={drawerChecklist?.folder}
                    />
                  ),
                },
                {
                  label: t("customer"),
                  value: (
                    <CustomerTitle customer={drawerChecklist?.customer} />
                  ),
                },
                {
                  label: t("Responsible"),
                  value:
                    drawerChecklist.responsible_executive?.displayTitle || "—",
                },
                {
                  label: t("Created"),
                  value: drawerChecklist.created_at
                    ? dateToString(drawerChecklist.created_at)
                    : "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>

      {createSelectorProps ? (
        <EnhancedChecklistSelector
          open={checklistDialogOpen}
          onClose={() => {
            if (!isCreatingChecklist) {
              setChecklistDialogOpen(false);
            }
          }}
          onSelect={handleChecklistSelect}
          defaultTitle={checklistDefaultTitle}
          showTitleField
          autoTitleFromType
          {...createSelectorProps}
        />
      ) : null}
    </>
  );
};
